import { useQuery } from '@tanstack/react-query';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import { Clock, Coins, Gauge } from 'lucide-react';
import { api, type VerdienstenObject } from '../../lib/api';
import { Card, Disclosure, Kpi } from '../ui';
import {
  ChartCard, KpiRow, TableCard, TabLoading, TabError, TabEmpty, CHART, ANIM, RATE_COLORS, truncate, euPerHour,
  TOOLTIP_STYLE, TOOLTIP_LABEL_STYLE, TOOLTIP_CURSOR, GRID_STROKE, AXIS_PROPS, AXIS_TICK,
} from './StatsShared';
import { formatMoney, formatHours } from '../../lib/utils';
import { useGeld } from '../../lib/geld';

type Rates = VerdienstenObject['rate_breakdown'];

export function VerdienstenTab({ year }: { year: number }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['stats-verdiensten', year],
    queryFn: () => api.stats.verdiensten(year),
  });
  const { bedrag, label } = useGeld();
  // Bedrag in de gekozen modus; netto via de netto/bruto-verhouding halverwege het jaar.
  const m = (b: number) => bedrag(b, `${year}-06-15`) ?? b;

  if (isLoading) return <TabLoading />;
  if (error || !data) return <TabError error={error} />;

  if (data.objects.length === 0) {
    return <TabEmpty title={`Nog geen gewerkte diensten in ${year}`} />;
  }

  const worked = data.objects.filter((o) => o.hours > 0);
  const perObject = worked.map((o) => ({
    name: truncate(o.object_naam, 18),
    full: o.object_naam,
    euro: m(o.effective_hourly_wage),
  }));

  return (
    <div className="space-y-4 sm:space-y-6">
      <KpiRow cols={3}>
        <Kpi
          icon={Gauge}
          label="Gemiddeld per uur"
          value={m(data.totals.effective_hourly_wage)}
          format={euPerHour}
          rows={[['Inclusief', 'toeslagen'], ['Soort', label.toLowerCase()]]}
        />
        <Kpi icon={Clock} label="Gewerkt" value={data.totals.hours} format={formatHours} hint={`In ${year}`} />
        <Kpi icon={Coins} label={`${label} uit diensten`} value={m(data.totals.bruto)} format={formatMoney} rows={[['Reiskosten', 'niet meegeteld']]} className="col-span-2 lg:col-span-1" />
      </KpiRow>

      <ChartCard
        title="Uurloon per locatie"
        subtitle={`${label} gedeeld door uren. Hoger betekent meer avond-, nacht- of weekenduren.`}
        height={Math.max(200, perObject.length * 36 + 30)}
      >
        <ResponsiveContainer>
          <BarChart data={perObject} layout="vertical" margin={{ top: 0, right: 64, left: 0, bottom: 0 }}>
            <CartesianGrid stroke={GRID_STROKE} horizontal={false} />
            <XAxis type="number" {...AXIS_PROPS} tickFormatter={(v) => `€${v}`} />
            <YAxis dataKey="name" type="category" {...AXIS_PROPS} width={124} />
            <Tooltip
              contentStyle={TOOLTIP_STYLE}
              labelStyle={TOOLTIP_LABEL_STYLE}
              cursor={TOOLTIP_CURSOR}
              labelFormatter={(_: unknown, payload: Array<{ payload?: { full?: string } }>) => payload?.[0]?.payload?.full ?? ''}
              formatter={(v: number) => [euPerHour(v), 'Per uur']}
            />
            <Bar
              dataKey="euro"
              fill={CHART.main}
              radius={[0, 4, 4, 0]}
              barSize={18}
              isAnimationActive={false}
              label={{ position: 'right', formatter: (v: number) => `€ ${v.toFixed(2).replace('.', ',')}`, ...AXIS_TICK, fill: '#CED2D8' }}
            />
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>

      <TableCard title="Per tarief, alle locaties" sub="Hoeveel uur je op elk percentage werkte en wat dat per uur opleverde.">
        <RateTable m={m} label={label} rates={data.rate_breakdown} total />
      </TableCard>

      <Card flush title="Per locatie" className="min-w-0">
        <div className="list border-t border-surface-line/60">
          {worked.map((o) => (
            <Disclosure
              key={`${o.object_id}-${o.object_naam}`}
              title={o.object_naam}
              meta={`${o.shifts} diensten, ${formatHours(o.hours)}${o.distance_km ? `, ${o.distance_km} km van huis` : ''}`}
              right={
                <div className="text-right">
                  <div className="t-num text-sm font-medium text-slate-100">{euPerHour(m(o.effective_hourly_wage))}</div>
                  <div className="t-num text-xs text-slate-400">{formatMoney(m(o.bruto))}</div>
                </div>
              }
            >
              <div className="-mx-4 overflow-x-auto sm:-mx-5">
                <RateTable m={m} label={label} rates={o.rate_breakdown} />
              </div>
            </Disclosure>
          ))}
        </div>
      </Card>
    </div>
  );
}

function RateTable({ rates, total, m, label }: { rates: Rates; total?: boolean; m: (b: number) => number; label: string }) {
  if (rates.length === 0) return <p className="px-4 pb-4 text-sm text-slate-400 sm:px-5">Geen uren op deze locatie.</p>;
  const maxHours = Math.max(...rates.map((r) => r.hours), 0.01);
  const sumHours = rates.reduce((s, r) => s + r.hours, 0);
  const sumBruto = rates.reduce((s, r) => s + r.bruto, 0);
  return (
    <table className="table">
      <thead>
        <tr>
          <th>Tarief</th>
          <th className="r">Uren</th>
          <th className="r hidden sm:table-cell">Aandeel</th>
          <th className="r">Per uur</th>
          <th className="r">{label}</th>
        </tr>
      </thead>
      <tbody>
        {rates.map((r) => {
          const color = RATE_COLORS[r.rate_type] ?? '#64748b';
          return (
            <tr key={r.rate_type}>
              <td className="min-w-[7rem]">
                <div className="flex items-center gap-2">
                  <span className="inline-block h-2 w-2 shrink-0 rounded-full" style={{ background: color }} aria-hidden />
                  <span className="truncate text-slate-200">{r.label}</span>
                </div>
                <div className="mt-1.5 h-1 overflow-hidden rounded-full bg-slate-700/60">
                  <div className="h-full rounded-full" style={{ width: `${(r.hours / maxHours) * 100}%`, background: color }} />
                </div>
              </td>
              <td className="r whitespace-nowrap">{formatHours(r.hours)}</td>
              <td className="r hidden text-slate-400 sm:table-cell">{String(r.pct).replace('.', ',')}%</td>
              <td className="r whitespace-nowrap font-medium text-slate-100">{euPerHour(m(r.effective_hourly_wage))}</td>
              <td className="r whitespace-nowrap">{formatMoney(m(r.bruto))}</td>
            </tr>
          );
        })}
      </tbody>
      {total && (
        <tfoot className="font-semibold text-slate-100">
          <tr>
            <td>Totaal</td>
            <td className="r whitespace-nowrap">{formatHours(sumHours)}</td>
            <td className="r hidden sm:table-cell">100%</td>
            <td className="r whitespace-nowrap">{sumHours > 0 ? euPerHour(m(sumBruto) / sumHours) : '–'}</td>
            <td className="r whitespace-nowrap">{formatMoney(m(sumBruto))}</td>
          </tr>
        </tfoot>
      )}
    </table>
  );
}
